import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type TooltipPlacement = 'right' | 'left' | 'top' | 'bottom'

type SmartTooltipProps = {
  content: React.ReactNode
  children: React.ReactElement
  disabled?: boolean
  offset?: number
  maxWidth?: number
  maxHeight?: number
  padding?: number
  closeDelayMs?: number
  /** Render only the positioned container without the default dark chrome,
   *  letting `content` bring its own styling (e.g. a light card). */
  bare?: boolean
}

export function SmartTooltip({
  content,
  children,
  disabled = false,
  offset = 10,
  maxWidth = 420,
  maxHeight = 260,
  padding = 12,
  closeDelayMs = 140,
  bare = false,
}: SmartTooltipProps) {
  const [open, setOpen] = useState(false)
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null)
  const [pos, setPos] = useState<{ left: number; top: number; placement: TooltipPlacement } | null>(null)

  const tipRef = useRef<HTMLDivElement | null>(null)
  const closeTimer = useRef<number | null>(null)

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimer.current = window.setTimeout(() => {
      setOpen(false)
      setAnchor(null)
      setPos(null)
    }, closeDelayMs)
  }

  const forceRecalc = () => setPos(null)

  // ESC закрывает
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') scheduleClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Пересчёт при скролле/ресайзе
  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', forceRecalc)
    window.addEventListener('scroll', forceRecalc, true)
    return () => {
      window.removeEventListener('resize', forceRecalc)
      window.removeEventListener('scroll', forceRecalc, true)
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open || !anchor) return
    const el = tipRef.current
    if (!el) return

    const vw = window.innerWidth
    const vh = window.innerHeight
    const rect = el.getBoundingClientRect()
    const tw = rect.width
    const th = rect.height

    const spaceRight = vw - anchor.x - padding
    const spaceLeft = anchor.x - padding
    const spaceBottom = vh - anchor.y - padding
    const spaceTop = anchor.y - padding

    // предпочтение направлений
    let placement: TooltipPlacement = 'right'
    if (spaceRight >= tw + offset) placement = 'right'
    else if (spaceLeft >= tw + offset) placement = 'left'
    else if (spaceBottom >= th + offset) placement = 'bottom'
    else placement = 'top'

    let left = anchor.x
    let top = anchor.y

    if (placement === 'right') {
      left = anchor.x + offset
      top = anchor.y - th / 2
    } else if (placement === 'left') {
      left = anchor.x - tw - offset
      top = anchor.y - th / 2
    } else if (placement === 'bottom') {
      left = anchor.x - tw / 2
      top = anchor.y + offset
    } else {
      left = anchor.x - tw / 2
      top = anchor.y - th - offset
    }

    const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))
    left = clamp(left, padding, vw - tw - padding)
    top = clamp(top, padding, vh - th - padding)

    setPos({ left, top, placement })
  }, [open, anchor, offset, padding, content])

  const childProps = {
    onMouseEnter: (e: React.MouseEvent) => {
      if (disabled) return
      clearCloseTimer()
      setAnchor({ x: e.clientX, y: e.clientY }) // фиксируем точку входа
      setOpen(true)
      children.props.onMouseEnter?.(e)
    },
    onMouseLeave: (e: React.MouseEvent) => {
      if (disabled) return
      scheduleClose()
      children.props.onMouseLeave?.(e)
    },
    onFocus: (e: React.FocusEvent) => {
      if (disabled) return
      clearCloseTimer()
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
      setAnchor({ x: r.left + r.width / 2, y: r.top + r.height / 2 })
      setOpen(true)
      children.props.onFocus?.(e)
    },
    onBlur: (e: React.FocusEvent) => {
      if (disabled) return
      scheduleClose()
      children.props.onBlur?.(e)
    },
  }

  return (
    <>
      {React.cloneElement(children, childProps)}

      {open && anchor && !disabled
        ? createPortal(
            <div
              ref={tipRef}
              onMouseEnter={() => clearCloseTimer()} // навёл на тултип: НЕ закрываем
              onMouseLeave={() => scheduleClose()}   // ушёл с тултипа: закрываем
              style={{
                position: 'fixed',
                left: pos?.left ?? -9999,
                top: pos?.top ?? -9999,
                zIndex: 99999,

                // bare: transparent pass-through — no size cap, no clipping, so the
                // content's own card (size, border, shadow, padding) renders in full.
                ...(bare
                  ? { overflow: 'visible' }
                  : {
                      maxWidth,
                      maxHeight,
                      overflow: 'auto',

                      padding: '10px 12px',
                      borderRadius: 12,
                      fontSize: 12.5,
                      lineHeight: 1.35,

                      background: 'rgba(18, 18, 18, 0.94)',
                      color: '#fff',
                      boxShadow: '0 14px 40px rgba(0,0,0,0.28)',
                      border: '1px solid rgba(255,255,255,0.08)',

                      overflowWrap: 'anywhere',
                      whiteSpace: 'pre-wrap',
                    }),
              }}
            >
              {content}
            </div>,
            document.body
          )
        : null}
    </>
  )
}